/**
 * Layout primitives that compose the dashboard's 4-zone shell.
 *
 *   • ThisWeekGroup    — Zone-2 wrapper with eyebrow + soft left-rail accent;
 *                        auto-hides when no children render.
 *   • TriadStat        — compact row for the C/M/LEI triad inside the unified PE card.
 *   • DetailsDisclosure — collapsible "Show details ▸" toggle for Zone-3 tertiary cards.
 *   • ErrorView         — the top-level error box that swaps in a tenant-unseeded
 *                         empty state when the backend is bare.
 *
 * Kept together because they're all small, share a single import surface,
 * and have no internal effects/state worth isolating into separate files.
 */
import React, { useState } from 'react';
import { ArrowRight, ClipboardCheck, Info } from 'lucide-react';
import PIEUnseededEmptyState, { isUnseededError } from '../PIEUnseededEmptyState';
import { pct } from '../format';
import { Perf } from '../PIEDisplayMode';

export function ThisWeekGroup({ children, hasRevival = false }) {
  // Children may render null (e.g. pulse widget when already logged
  // can be present or absent). We do a light filter to decide whether
  // to show the section header at all.
  const rendered = React.Children.toArray(children).filter(Boolean);
  if (rendered.length === 0) return null;
  return (
    <section
      data-testid="pie-zone-this-week"
      style={{
        marginBottom: 28,
        paddingLeft: 18,
        borderLeft: hasRevival
          ? '3px solid var(--pie-danger, #DC2626)'
          : '3px solid var(--pie-accent)',
        position: 'relative',
      }}
    >
      <div
        className="pie-score-eyebrow"
        style={{ marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <ClipboardCheck size={11} aria-hidden />
        <span>This week</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </section>
  );
}

export function TriadStat({ label, value, sub, testid, onInfo, highlight = false }) {
  const pctNum = Math.max(0, Math.min(100, (value || 0) * 100));
  return (
    <div data-testid={testid} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span
          className="pie-score-eyebrow"
          style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {label}
          {onInfo && (
            <button
              type="button"
              className="pie-eyebrow-info"
              onClick={onInfo}
              aria-label={`What is ${label}?`}
              title={`What is ${label}?`}
              data-testid={`${testid}-info`}
            >
              <Info size={10} />
            </button>
          )}
        </span>
        <span
          className="pie-mono"
          style={{
            fontSize: 16, fontWeight: 700,
            color: highlight ? 'var(--pie-accent)' : 'var(--pie-text)',
          }}
        >
          {value !== undefined ? (
            <Perf value={value} kind="pct01" testid={`${testid}-value`} />
          ) : (
            '—'
          )}
        </span>
      </div>
      <div className="pie-scorebar" style={{ height: 4 }}>
        <div className="pie-scorebar-fill" style={{ width: `${pctNum}%` }} />
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--pie-text-muted)' }}>{sub}</div>
    </div>
  );
}

export function DetailsDisclosure({ label, testid, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid} style={{ marginTop: 20 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12,
          padding: '12px 16px',
          border: '1px solid var(--pie-border)',
          background: 'var(--pie-surface)',
          borderRadius: 10,
          fontSize: 13, fontWeight: 600, color: 'var(--pie-text)',
          cursor: 'pointer',
          transition: 'background 0.14s ease, border-color 0.14s ease',
        }}
      >
        <span>{label}</span>
        <ArrowRight
          size={14}
          aria-hidden
          style={{
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.16s ease',
            color: 'var(--pie-accent)',
          }}
        />
      </button>
      {open && (
        <div data-testid={`${testid}-body`} style={{ marginTop: 14 }}>
          {children}
        </div>
      )}
    </div>
  );
}

export function ErrorView({ err }) {
  if (isUnseededError(err)) return <PIEUnseededEmptyState />;
  return (
    <div className="pie-card" style={{ marginTop: 24, color: 'var(--pie-danger)' }} data-testid="pie-error">
      Something went wrong: {err}
    </div>
  );
}
